import { DummyWalkerNPC, InspectorNPC, WorldPoint } from '../domain/types.ts';
import { getRandomNpcDialogue, getRandomNpcEmote, ARCHETYPE_CONFIG } from '../entities/npc.ts';
import { calcReadingDuration } from '../services/dialogService.ts';

/**
 * SPEECH COORDINATOR — Hệ thống Điều phối Hội thoại & Lọc Sự kiện Vỉa Hè
 * - Giải quyết triệt để vấn đề: Nhiều NPC cùng phát ngôn một lúc, cùng một câu hardcode.
 * - Áp dụng Cooldown Toàn Cục (Global Cooldown) 1.5s - 2.5s theo ngữ cảnh.
 * - Cơ chế Bầu chọn Người Phát Ngôn (Speaker Election): 1 Main Speaker duy nhất, người khác im lặng hoặc hiện emote (!, ?).
 * - Giới hạn số lượng bong bóng thoại trên màn hình không quá 2-3 bubbles cùng lúc.
 */
export class SpeechCoordinator {
  private contextCooldowns: Map<string, number> = new Map();
  private readonly MAX_CONCURRENT_BUBBLES = 3;
  private readonly DEFAULT_CONTEXT_COOLDOWN_MS = 2000;

  public update(dt: number): void {
    // Giảm dần các cooldown theo thời gian
    for (const [ctx, remaining] of this.contextCooldowns.entries()) {
      const next = remaining - dt * 1000;
      if (next <= 0) {
        this.contextCooldowns.delete(ctx);
      } else {
        this.contextCooldowns.set(ctx, next);
      }
    }
  }

  /**
   * Kiểm tra xem ngữ cảnh có đang trong thời gian Cooldown Toàn Cục không
   */
  public canTriggerEvent(context: string): boolean {
    return !this.contextCooldowns.has(context);
  }

  /**
   * Đặt Cooldown Toàn Cục cho ngữ cảnh
   */
  public setContextCooldown(context: string, cooldownMs = this.DEFAULT_CONTEXT_COOLDOWN_MS): void {
    this.contextCooldowns.set(context, cooldownMs);
  }

  /**
   * Đếm tổng số bong bóng thoại đang hiển thị trên màn hình
   */
  public countActiveBubbles(walkers: DummyWalkerNPC[], inspectors?: InspectorNPC[]): number {
    let count = 0;
    for (const w of walkers) {
      if (w.speechText) count++;
    }
    if (inspectors) {
      for (const i of inspectors) {
        if (i.speechText) count++;
      }
    }
    return count;
  }

  /**
   * Bầu chọn Người Phát Ngôn Chính (Speaker Election):
   * - Gom nhóm các NPC chứng kiến sự kiện
   * - Lọc ra người phù hợp nhất dựa trên tính cách & khoảng cách
   * - 1 Main Speaker nói câu thoại ngẫu nhiên từ Dialogue Pool
   * - Các nhân chứng còn lại chỉ hiện emote (!, ?) ngắn hạn hoặc giữ im lặng
   */
  public electAndSpeakGroup(
    candidates: DummyWalkerNPC[],
    context: 'defect' | 'pooping' | 'cooperate',
    targetPos: WorldPoint,
    allWalkers: DummyWalkerNPC[],
    inspectors?: InspectorNPC[]
  ): DummyWalkerNPC | null {
    if (candidates.length === 0) return null;

    // 1. Kiểm tra Cooldown Toàn Cục của sự kiện
    if (!this.canTriggerEvent(context)) {
      return null;
    }

    // 2. Kiểm tra giới hạn bong bóng trên màn hình (tối đa 2-3 bubble)
    const currentBubbles = this.countActiveBubbles(allWalkers, inspectors);
    if (currentBubbles >= this.MAX_CONCURRENT_BUBBLES) {
      return null;
    }

    // 3. Lọc các ứng viên không trong cooldown cá nhân
    const eligible = candidates.filter((w) => (w.alarmCooldownTimer ?? 0) <= 0);
    if (eligible.length === 0) return null;

    // Trọng số ưu tiên Archetype theo phân tích Spatial Game Theory
    const archetypeWeight: Record<string, number> = {
      ba_tam: 10,
      bac_huu_tri: 9,
      dan_phong: 8,
      gen_z: 7,
      altruist: 6,
      tit_for_tat: 5,
      nguoi_tho_o: 2,
      defector: 1,
    };

    // 4. Bầu chọn Main Speaker có điểm số cao nhất (tính cách + cự ly gần)
    let bestCandidate: DummyWalkerNPC = eligible[0]!;
    let bestScore = -Infinity;

    for (const candidate of eligible) {
      const dist = Math.hypot(candidate.x - targetPos.x, candidate.y - targetPos.y);
      const weight = archetypeWeight[candidate.archetype] || 3;
      const score = weight * 10 - dist;

      if (score > bestScore) {
        bestScore = score;
        bestCandidate = candidate;
      }
    }

    // 5. Gán câu thoại phong phú ngẫu nhiên cho Main Speaker
    const mainLine = getRandomNpcDialogue(bestCandidate.archetype, context);
    const cfg = ARCHETYPE_CONFIG[bestCandidate.archetype];
    const duration = calcReadingDuration(mainLine);

    bestCandidate.speechText = mainLine;
    bestCandidate.speechColor = cfg.speechColor;
    bestCandidate.speechTimer = duration;
    bestCandidate.alarmCooldownTimer = 6500; // Cooldown cá nhân 6.5s

    // 6. Các nhân chứng còn lại: Chỉ chọn tối đa 1 người gần nhất phát emote biểu tượng ngắn (!)
    // để tạo hiệu ứng xôn xao đám đông nhưng TUYỆT ĐỐI KHÔNG chiếm diện tích bong bóng
    let emoteGiven = 0;
    for (const other of eligible) {
      if (other.id === bestCandidate.id) continue;
      if (other.speechText) continue;

      if (emoteGiven < 1 && currentBubbles + 2 <= this.MAX_CONCURRENT_BUBBLES) {
        const emote = getRandomNpcEmote(other.archetype);
        const otherCfg = ARCHETYPE_CONFIG[other.archetype];
        other.speechText = emote;
        other.speechColor = otherCfg.speechColor;
        other.speechTimer = 1600; // Emote tắt nhanh sau 1.6s
        other.alarmCooldownTimer = 4000;
        emoteGiven++;
      } else {
        // Giữ im lặng, chỉ gán cooldown nhẹ để không bị spam
        other.alarmCooldownTimer = 3500;
      }
    }

    // 7. Đặt Cooldown Toàn Cục cho ngữ cảnh (2.0s - 2.5s)
    const cooldownDuration = context === 'defect' ? 2000 : 2500;
    this.setContextCooldown(context, cooldownDuration);

    return bestCandidate;
  }

  /**
   * Điều phối khi nghe thấy sóng âm tri hô (Alarm Sound Wave):
   * - Gom các Dân Phòng hoặc Hàng Xóm gần đó
   * - Bầu 1 người phát ngôn câu thoại ngẫu nhiên
   * - Những người khác chỉ đổi hướng chạy hoặc hiện emote báo động
   */
  public handleSoundWaveHeard(
    hearers: DummyWalkerNPC[],
    wavePos: WorldPoint,
    allWalkers: DummyWalkerNPC[],
    inspectors?: InspectorNPC[]
  ): void {
    if (hearers.length === 0) return;

    // Giữ cooldown toàn cục cho phản ứng nghe sóng âm (1.8s)
    if (!this.canTriggerEvent('alarm_heard')) {
      return;
    }

    const currentBubbles = this.countActiveBubbles(allWalkers, inspectors);
    if (currentBubbles >= this.MAX_CONCURRENT_BUBBLES) {
      return;
    }

    const eligible = hearers.filter((w) => (w.alarmCooldownTimer ?? 0) <= 0);
    if (eligible.length === 0) return;

    // Ưu tiên Dân Phòng > Bác Hưu Trí > Khác
    eligible.sort((a, b) => {
      const wa = a.archetype === 'dan_phong' ? 10 : a.archetype === 'bac_huu_tri' ? 8 : 4;
      const wb = b.archetype === 'dan_phong' ? 10 : b.archetype === 'bac_huu_tri' ? 8 : 4;
      return wb - wa;
    });

    const speaker = eligible[0]!;
    const line = getRandomNpcDialogue(speaker.archetype, 'alarm_heard');
    const cfg = ARCHETYPE_CONFIG[speaker.archetype];
    speaker.speechText = line;
    speaker.speechColor = cfg.speechColor;
    speaker.speechTimer = calcReadingDuration(line);
    speaker.alarmCooldownTimer = 5000;

    // Những người còn lại: nếu là Dân phòng thì chỉ hiện emote 🚨
    for (let i = 1; i < eligible.length; i++) {
      const other = eligible[i]!;
      if (!other.speechText && i === 1 && currentBubbles + 2 <= this.MAX_CONCURRENT_BUBBLES) {
        other.speechText = '🚨';
        other.speechColor = '#2979ff';
        other.speechTimer = 1500;
      }
      other.alarmCooldownTimer = 4000;
    }

    this.setContextCooldown('alarm_heard', 1800);
  }

  /**
   * Điều phối cảnh báo tổ trưởng / inspectors khi chó sủa
   */
  public handleInspectorsBarkAlert(
    inspectors: InspectorNPC[],
    targetPos: WorldPoint,
    getDialogLine: () => string
  ): void {
    if (inspectors.length === 0) return;

    if (!this.canTriggerEvent('inspector_bark')) {
      return;
    }

    // Chọn 1 inspector gần nhất phát ngôn
    inspectors.sort((a, b) => {
      const da = Math.hypot(a.x - targetPos.x, a.y - targetPos.y);
      const db = Math.hypot(b.x - targetPos.x, b.y - targetPos.y);
      return da - db;
    });

    const chosen = inspectors[0]!;
    const line = getDialogLine();
    chosen.speechText = line;
    chosen.speechTimer = calcReadingDuration(line);

    for (let i = 1; i < inspectors.length; i++) {
      inspectors[i]!.speechText = '❓';
      inspectors[i]!.speechTimer = 1500;
    }

    this.setContextCooldown('inspector_bark', 2500);
  }
}
